import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { X, Plus, Loader2 } from 'lucide-react'
import { api } from '@/lib/api'
import type { Dish, RecipeIngredient } from '@/types/models'

interface Props {
  dish: Dish
  onClose: () => void
  onSaved: (updated: Dish) => void
}

interface IngredientDraft {
  name: string
  quantity: number
  unit: string
  dish_id: string | null
}

const COMMON_UNITS = ['g', 'kg', 'ml', 'L', 'tbsp', 'tsp', 'cup', 'oz', 'lb', 'serving', 'piece', 'slice', 'clove', 'can']

const NUTRITION_FIELDS = [
  { key: 'calories' as const, label: 'Calories', unit: 'kcal', step: 1 },
  { key: 'protein_g' as const, label: 'Protein', unit: 'g', step: 0.1 },
  { key: 'carbs_g' as const, label: 'Carbs', unit: 'g', step: 0.1 },
  { key: 'fat_g' as const, label: 'Fat', unit: 'g', step: 0.1 },
  { key: 'fiber_g' as const, label: 'Fiber', unit: 'g', step: 0.1 },
]

export default function RecipeEditor({ dish, onClose, onSaved }: Props) {
  const { getToken } = useAuth()
  const [tab, setTab] = useState<'ingredients' | 'nutrition'>('ingredients')
  const [recipeServings, setRecipeServings] = useState(dish.recipe_servings ?? 1)
  const [ingredients, setIngredients] = useState<IngredientDraft[]>(
    dish.recipe_ingredients?.length > 0
      ? dish.recipe_ingredients
      : []
  )
  const [nutrition, setNutrition] = useState({
    calories: dish.calories != null ? String(dish.calories) : '',
    protein_g: dish.protein_g != null ? String(dish.protein_g) : '',
    carbs_g: dish.carbs_g != null ? String(dish.carbs_g) : '',
    fat_g: dish.fat_g != null ? String(dish.fat_g) : '',
    fiber_g: dish.fiber_g != null ? String(dish.fiber_g) : '',
  })
  const [autoFillNote, setAutoFillNote] = useState<string | null>(null)

  const saveMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const body: Record<string, unknown> = {
        recipe_servings: recipeServings,
        recipe_ingredients: ingredients,
      }
      if (nutrition.calories !== '') body.calories = Number(nutrition.calories)
      if (nutrition.protein_g !== '') body.protein_g = Number(nutrition.protein_g)
      if (nutrition.carbs_g !== '') body.carbs_g = Number(nutrition.carbs_g)
      if (nutrition.fat_g !== '') body.fat_g = Number(nutrition.fat_g)
      if (nutrition.fiber_g !== '') body.fiber_g = Number(nutrition.fiber_g)
      return api.patch<{ data: Dish }>(`/dishes/${dish.id}`, body, token)
    },
    onSuccess: (res) => {
      onSaved((res as { data: Dish }).data)
    },
  })

  function addIngredient() {
    setIngredients(prev => [...prev, { name: '', quantity: 1, unit: 'g', dish_id: null }])
  }

  function updateIngredient(index: number, updates: Partial<IngredientDraft>) {
    setIngredients(prev => prev.map((ing, i) => i === index ? { ...ing, ...updates } : ing))
  }

  function removeIngredient(index: number) {
    setIngredients(prev => prev.filter((_, i) => i !== index))
  }

  function handleAutoFill() {
    const linked = ingredients.filter(ing => ing.dish_id != null)
    if (linked.length === 0) {
      setAutoFillNote('Link ingredients to dishes to enable auto-fill. Search for an ingredient name to find linked dishes.')
      return
    }
    if (linked.length < ingredients.length) {
      setAutoFillNote(`${ingredients.length - linked.length} ingredient(s) couldn't be auto-calculated — they aren't linked to tracked dishes.`)
    } else {
      setAutoFillNote(null)
    }
    // Note: actual summation would require fetching macros for each linked dish_id.
    // The backend handles this transparently via recipe_ingredients when generating shopping lists.
    setAutoFillNote(prev =>
      (prev ? prev + ' ' : '') + 'Macro auto-fill from linked dishes is not yet supported in this version.'
    )
  }

  const hasChanges = true // always allow save

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-900">{dish.name}</h3>
            <p className="text-xs text-gray-400 mt-0.5">Recipe Editor</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-5">
          {(['ingredients', 'nutrition'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`py-2.5 px-4 text-sm font-medium border-b-2 transition-colors capitalize ${
                tab === t ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >{t}</button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === 'ingredients' && (
            <div className="space-y-5">
              {/* Recipe servings */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Recipe makes</label>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setRecipeServings(s => Math.max(1, s - 1))}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600"
                  >−</button>
                  <span className="w-8 text-center font-medium text-sm">{recipeServings}</span>
                  <button
                    onClick={() => setRecipeServings(s => s + 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600"
                  >+</button>
                </div>
                <span className="text-sm text-gray-500">servings</span>
              </div>

              {/* Ingredients */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Ingredients</p>
                {ingredients.length === 0 && (
                  <p className="text-sm text-gray-400 py-2">No ingredients yet.</p>
                )}
                <div className="space-y-2">
                  {ingredients.map((ing, index) => (
                    <IngredientRow
                      key={index}
                      ingredient={ing}
                      onChange={updates => updateIngredient(index, updates)}
                      onRemove={() => removeIngredient(index)}
                    />
                  ))}
                </div>
                <button
                  onClick={addIngredient}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 text-sm text-brand-600 hover:bg-brand-50 rounded-xl border-2 border-dashed border-brand-200 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add ingredient
                </button>
              </div>
            </div>
          )}

          {tab === 'nutrition' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Nutrition <strong>per serving</strong> of this dish.</p>

              {NUTRITION_FIELDS.map(({ key, label, unit, step }) => (
                <div key={key} className="flex items-center gap-3">
                  <label className="w-20 text-sm font-medium text-gray-700">{label}</label>
                  <input
                    type="number"
                    min={0}
                    step={step}
                    value={nutrition[key]}
                    onChange={e => setNutrition(prev => ({ ...prev, [key]: e.target.value }))}
                    className="w-28 input text-sm"
                    placeholder={`0 ${unit}`}
                  />
                  <span className="text-xs text-gray-400">{unit}</span>
                </div>
              ))}

              {autoFillNote && (
                <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">{autoFillNote}</p>
              )}
              <button
                onClick={handleAutoFill}
                className="text-sm text-brand-600 hover:text-brand-700 hover:underline"
              >
                Auto-fill from ingredients →
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
            Cancel
          </button>
          {saveMutation.isError && (
            <p className="text-xs text-red-500">
              {(saveMutation.error as Error)?.message ?? 'Failed to save'}
            </p>
          )}
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            {saveMutation.isPending
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
              : 'Save recipe'
            }
          </button>
        </div>
      </div>
    </div>
  )
}

// Per-row ingredient editor with dish search autocomplete
interface IngredientRowProps {
  ingredient: IngredientDraft
  onChange: (updates: Partial<IngredientDraft>) => void
  onRemove: () => void
}

function IngredientRow({ ingredient, onChange, onRemove }: IngredientRowProps) {
  const { getToken } = useAuth()
  const [searchQ, setSearchQ] = useState(ingredient.name)
  const [showDropdown, setShowDropdown] = useState(false)

  const { data: searchRes } = useQuery({
    queryKey: ['dish-search-ing', searchQ],
    queryFn: async () => {
      if (searchQ.trim().length < 2) return { data: [] }
      const token = await getToken()
      return api.get<{ data: Dish[] }>(`/dishes/search?q=${encodeURIComponent(searchQ)}&limit=5`, token!)
    },
    enabled: showDropdown && searchQ.trim().length >= 2,
  })
  const suggestions = (searchRes as { data: Dish[] } | undefined)?.data ?? []

  return (
    <div className="flex items-center gap-2">
      {/* Name with dish autocomplete */}
      <div className="relative flex-1 min-w-0">
        <input
          type="text"
          value={searchQ}
          onChange={e => {
            setSearchQ(e.target.value)
            onChange({ name: e.target.value, dish_id: null })
            setShowDropdown(true)
          }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          placeholder="Ingredient name"
          className="input text-sm w-full"
        />
        {showDropdown && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-36 overflow-y-auto">
            {suggestions.map(d => (
              <button
                key={d.id}
                type="button"
                onMouseDown={() => {
                  setSearchQ(d.name)
                  onChange({ name: d.name, dish_id: d.id })
                  setShowDropdown(false)
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
              >
                <span>{d.name}</span>
                {d.calories != null && (
                  <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{d.calories} kcal</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Quantity */}
      <input
        type="number"
        min={0}
        step={0.1}
        value={ingredient.quantity}
        onChange={e => onChange({ quantity: parseFloat(e.target.value) || 0 })}
        className="w-16 input text-sm text-center"
        placeholder="Qty"
      />

      {/* Unit */}
      <select
        value={ingredient.unit}
        onChange={e => onChange({ unit: e.target.value })}
        className="w-24 input text-sm"
      >
        {COMMON_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
      </select>

      {/* Remove */}
      <button
        onClick={onRemove}
        className="p-1.5 hover:bg-red-50 rounded text-gray-300 hover:text-red-400 flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
